import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ROLE_HOME = { worker: "/crew", manager: "/foreman", admin: "/admin" };

// The History API cannot be asked "is there a forward entry?", so we track it
// ourselves. Going Back creates one; any navigation we did not cause destroys
// it, which is exactly how the browser's own forward stack behaves.
//
// Module-level rather than component state because this bar unmounts and
// remounts on every screen change -- the count has to outlive the component.
//
// Deliberately conservative: pressing the phone's own back gesture also creates
// a forward entry we cannot see, so Forward stays hidden. Under-showing a link
// is much better than offering one that does nothing when tapped.
let forwardDepth = 0;
let visits = 0;
let selfNav = false;

export function PageNav() {
    const navigate = useNavigate();
    const location = useLocation();
    const { employee } = useAuth();
    const [, bump] = useState(0);

    useEffect(() => {
        if (selfNav) {
            selfNav = false;
        } else {
            // A fresh push wipes whatever was ahead of us.
            forwardDepth = 0;
            visits += 1;
        }
        bump((n) => n + 1);
    }, [location.key]);

    const home = ROLE_HOME[employee?.role] || "/";
    const atHome = location.pathname === home;

    const goBack = () => {
        selfNav = true;
        if (visits > 1) {
            forwardDepth += 1;
            navigate(-1);
        } else {
            // Opened straight onto this screen (a notification link, a bookmark,
            // a refresh). There is nothing behind us in this session, so Back
            // means "up to my home screen" rather than out of the app entirely.
            navigate(home);
        }
    };

    const goForward = () => {
        selfNav = true;
        forwardDepth = Math.max(0, forwardDepth - 1);
        navigate(1);
    };

    // Nothing useful to offer on the home screen with no history behind it.
    if (atHome && visits <= 1 && forwardDepth === 0) return null;

    return (
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between" data-testid="page-nav">
            {atHome && visits <= 1 ? <span /> : (
                <button
                    onClick={goBack}
                    className="inline-flex items-center gap-0.5 -ml-1 py-0.5 text-sm font-bold text-brand-bone/85 hover:text-brand-bone hover:underline"
                    data-testid="page-nav-back"
                >
                    <ChevronLeft className="h-4 w-4" /> Back
                </button>
            )}
            {forwardDepth > 0 && (
                <button
                    onClick={goForward}
                    className="inline-flex items-center gap-0.5 -mr-1 py-0.5 text-sm font-bold text-brand-bone/85 hover:text-brand-bone hover:underline"
                    data-testid="page-nav-forward"
                >
                    Forward <ChevronRight className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}
